import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Modal, Card, Input, Empty, Spin, Button, Tag, Space, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { createOrderActions } from "../../../../store/slices/create-order-slice/create.orders.slice";
import { availableDishesActions } from "../../../../store/slices/menu-slices";
import { ImageReusable, SearchableSelect } from "../../../../ui";
import { groupDishesByParentChild } from "../../../../helpers/groupDishesByParentChild";
import { dishesService } from "../../../../services/dishes-services/dishes.service";
import { cycleAvailableDishesService } from "../../../../services/menu-services/cycleAvailableDishes.service";
import css from "./SimpleReplaceDishModal.module.css";

function getCycleDay(date, startDate, lengthDays) {
  const diff = dayjs(date).diff(dayjs(startDate), "day");
  return ((diff % lengthDays) + lengthDays) % lengthDays + 1;
}

/**
 * SimpleReplaceDishModal - A simplified component for replacing dishes
 *
 * Props:
 * @param {Object} dish - The current dish object
 * @param {string} mealId - The meal ID
 * @param {string} packId - The pack ID
 * @param {string} date - The date string
 * @param {boolean} visible - Modal visibility
 * @param {Function} onClose - Callback to close modal
 * @param {string} [dishType] - Optional dish type filter (e.g. DishType.PROTEIN, DishType.SAUCE)
 * @param {Function} [onSelectDish] - Optional custom selection handler (overrides default Redux dispatch)
 */
const SimpleReplaceDishModal = ({
  dish,
  mealId,
  packId,
  date,
  visible,
  onClose,
  dishType,
  onSelectDish,
}) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const { menuFormat, cycleConfig } = useSelector((store) => store.appSettings);

  const [searchValue, setSearchValue] = useState("");
  const [availableDishes, setAvailableDishes] = useState([]);
  const [cycleAllDishes, setCycleAllDishes] = useState([]); // full list for client-side search
  const [isLoading, setIsLoading] = useState(false);
  const [selectedVariants, setSelectedVariants] = useState({});

  const { availableDishesArray, availableDishesArrayFiltered, status } =
    useSelector((store) => store.availableDishes);

  const isTypedMode = !!dishType;
  const isCycleMode = menuFormat === "period_based";

  // Fetch cycle available dishes (used for both typed and non-typed in cycle mode)
  const fetchCycleDishes = useCallback(async () => {
    try {
      setIsLoading(true);
      const cycleDay = cycleConfig?.startDate
        ? getCycleDay(date, cycleConfig.startDate, cycleConfig.lengthDays || 7)
        : 1;
      const items = await cycleAvailableDishesService.getByDay(cycleDay);
      let dishes = (items || []).map((item) => item.dish).filter(Boolean);
      if (dishType) {
        dishes = dishes.filter((d) => d.type === dishType);
      }
      const mapped = dishes.map((d) => ({ dish: d, id: d.id, childDishes: [] }));
      setCycleAllDishes(mapped);
      setAvailableDishes(mapped);
    } catch (err) {
      console.error("Failed to fetch cycle available dishes", err);
      setAvailableDishes([]);
      setCycleAllDishes([]);
    } finally {
      setIsLoading(false);
    }
  }, [date, dishType, cycleConfig]);

  // Fetch typed dishes (protein/sauce) from global catalog — database mode only
  const fetchTypedDishes = useCallback(async (word = "") => {
    try {
      setIsLoading(true);
      const res = await dishesService.getAllDishesPaginated({
        page: 1,
        word,
        type: dishType,
      });
      const dishes = res?.data || res || [];
      setAvailableDishes(
        Array.isArray(dishes)
          ? dishes.map((d) => ({ dish: d, id: d.id, childDishes: [] }))
          : []
      );
    } catch (err) {
      console.error("Failed to fetch typed dishes", err);
      setAvailableDishes([]);
    } finally {
      setIsLoading(false);
    }
  }, [dishType]);

  // Fetch dishes when modal opens
  useEffect(() => {
    if (!visible) return;

    if (isCycleMode) {
      fetchCycleDishes();
    } else if (isTypedMode) {
      fetchTypedDishes("");
    } else if (date) {
      setIsLoading(true);
      dispatch(availableDishesActions.getAllAvailableDishes({ currentData: date }));
    }

    return () => {
      if (!isTypedMode && !isCycleMode) {
        dispatch(availableDishesActions.clearSearch());
      }
      setSearchValue("");
      setAvailableDishes([]);
      setCycleAllDishes([]);
    };
  }, [visible, date, dispatch, isTypedMode, isCycleMode, fetchTypedDishes, fetchCycleDishes]);

  // Process and group dishes — database non-typed mode only
  useEffect(() => {
    if (isTypedMode || isCycleMode) return;
    if (availableDishesArray && availableDishesArray.length > 0) {
      const dishesToProcess =
        availableDishesArrayFiltered.length > 0
          ? availableDishesArrayFiltered
          : availableDishesArray;
      const grouped = groupDishesByParentChild(dishesToProcess);
      setAvailableDishes(grouped);
      setIsLoading(false);
    }
  }, [availableDishesArray, availableDishesArrayFiltered, isTypedMode, isCycleMode]);

  // Initialize selected variants when available dishes change
  useEffect(() => {
    if (isTypedMode || isCycleMode) return;
    const initialVariants = {};
    availableDishes.forEach((d) => {
      if (d.childDishes && d.childDishes.length > 0) {
        initialVariants[d.dish.id] = d.childDishes[0];
      }
    });
    setSelectedVariants(initialVariants);
  }, [availableDishes, isTypedMode, isCycleMode]);

  // Handle search
  const handleSearch = (value) => {
    setSearchValue(value);
    if (isCycleMode) {
      // Client-side filter on the full cycle list
      if (!value.trim()) {
        setAvailableDishes(cycleAllDishes);
      } else {
        const q = value.trim().toLowerCase();
        setAvailableDishes(
          cycleAllDishes.filter((item) => {
            const name =
              item.dish?.name?.[currentLanguage] || item.dish?.name?.en || "";
            return name.toLowerCase().includes(q);
          })
        );
      }
    } else if (isTypedMode) {
      fetchTypedDishes(value);
    } else {
      dispatch(availableDishesActions.filteredDishes(value));
    }
  };

  // Handle variant change
  const handleVariantChange = (parentDishId, selectedVariant) => {
    setSelectedVariants((prev) => ({
      ...prev,
      [parentDishId]: selectedVariant,
    }));
  };

  // Handle dish selection
  const handleSelectDish = (selectedDish) => {
    if (onSelectDish) {
      onSelectDish(selectedDish);
      onClose();
      return;
    }

    const replacementData = {
      replacedFrom: dish?.dish?.id || dish?.dishId,
      replacedTo: selectedDish?.dish?.id || selectedDish?.dishId,
      packId,
      mealId,
      isReplaced: true,
      date,
      dish: selectedDish,
    };

    dispatch(createOrderActions.setOneReplacedDish(replacementData));
    onClose();
  };

  const getModalTitle = () => {
    const badge = (
      <Tag color={isCycleMode ? "blue" : "green"} style={{ marginLeft: 8 }}>
        {isCycleMode ? t("Cycle Menu") : t("Date Menu")}
      </Tag>
    );
    let label = t("selectReplacement");
    if (dishType === "PROTEIN") label = t("Select Protein Dish");
    if (dishType === "SAUCE") label = t("Select Sauce Dish");
    return (
      <Space align="center">
        <Typography.Text strong>{label}</Typography.Text>
        {badge}
      </Space>
    );
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      title={getModalTitle()}
      className={css.modal}
    >
      {/* Current Dish Info */}
      {dish && (
        <Card className={css.currentDishCard}>
          <div className={css.currentDishContent}>
            <div className={css.currentDishImage}>
              <ImageReusable
                src={dish?.dish?.img || dish?.img}
                alt={dish?.dish?.name?.[currentLanguage] || "Current Dish"}
                className={css.dishImg}
              />
            </div>
            <div className={css.currentDishInfo}>
              <h3>{t("currentDish")}:</h3>
              <p className={css.dishName}>
                {dish?.dish?.name?.[currentLanguage] ||
                  dish?.name?.[currentLanguage] ||
                  "Unknown Dish"}
              </p>
              <div className={css.nutritionTags}>
                <Tag color="orange">
                  {dish?.dish?.energy || dish?.energy} {t("calories")}
                </Tag>
                <Tag color="blue">
                  {dish?.dish?.weight || dish?.weight}g
                </Tag>
                <Tag>P: {dish?.dish?.proteins || dish?.proteins}g</Tag>
                <Tag>F: {dish?.dish?.fats || dish?.fats}g</Tag>
                <Tag>C: {dish?.dish?.carbs || dish?.carbs}g</Tag>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Search Input */}
      <div className={css.searchContainer}>
        <Input
          size="large"
          placeholder={t("Search dishes...")}
          prefix={<SearchOutlined />}
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
          allowClear
        />
      </div>

      {/* Available Dishes Grid */}
      <div className={css.dishesContainer}>
        {isLoading || (!isTypedMode && !isCycleMode && status === "Loading") ? (
          <div className={css.loadingContainer}>
            <Spin size="large" />
            <p>{t("Loading available dishes...")}</p>
          </div>
        ) : availableDishes.length === 0 ? (
          <Empty description={t("No available dishes for replacement")} />
        ) : (
          <div className={css.dishGrid}>
            {availableDishes.map((dishItem) => {
              const selectedVariant = selectedVariants[dishItem.dish.id] || dishItem;
              const displayDish = selectedVariant.dish;
              const hasVariants =
                !isTypedMode &&
                !isCycleMode &&
                dishItem.childDishes &&
                dishItem.childDishes.length > 0;

              return (
                <Card
                  key={dishItem.id || dishItem.dish.id}
                  hoverable
                  className={css.dishCard}
                  cover={
                    <div className={css.dishImageWrapper}>
                      <ImageReusable
                        src={displayDish.img}
                        alt={displayDish.name?.[currentLanguage] || "Dish"}
                        className={css.dishImage}
                      />
                    </div>
                  }
                >
                  <Card.Meta
                    title={
                      <div className={css.dishTitle}>
                        {displayDish.name?.[currentLanguage] ||
                          displayDish.name?.en ||
                          "Unknown Dish"}
                        {hasVariants && (
                          <Tag color="purple" className={css.variantTag}>
                            {dishItem.childDishes.length} {t("variants")}
                          </Tag>
                        )}
                      </div>
                    }
                    description={
                      <div className={css.dishDescription}>
                        {/* Variant Selector */}
                        {hasVariants && (
                          <div className={css.variantSelector}>
                            <SearchableSelect
                              options={dishItem.childDishes?.map((variant) => ({
                                value: variant.dish.id,
                                label: `${variant.dish.energy} ${t("calories")}`,
                                dish: variant,
                              }))}
                              value={selectedVariant.dish.id}
                              onChange={(value) => {
                                const selected = dishItem.childDishes?.find(
                                  (child) => child.dish.id === value
                                );
                                if (selected) {
                                  handleVariantChange(dishItem.dish.id, selected);
                                }
                              }}
                              placeholder={t("Select Variant")}
                              className={css.variantDropdown}
                              getOptionLabel={(option) => option.label}
                              getOptionValue={(option) => option.value}
                            />
                          </div>
                        )}

                        <div className={css.nutritionInfo}>
                          <span className={css.caloriesBadge}>
                            {displayDish.energy} {t("calories")}
                          </span>
                          <span className={css.weightBadge}>
                            {displayDish.weight}g
                          </span>
                        </div>
                        <div className={css.macros}>
                          <span>P: {displayDish.proteins}g</span>
                          <span>F: {displayDish.fats}g</span>
                          <span>C: {displayDish.carbs}g</span>
                        </div>
                      </div>
                    }
                  />
                  <Button
                    type="primary"
                    block
                    className={css.selectBtn}
                    onClick={() => handleSelectDish(selectedVariant)}
                  >
                    {t("select")}
                  </Button>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
};

export { SimpleReplaceDishModal };
